<template>
    <div class="main">
        <div class="admin-section">
            <div class="container">
                <div class="row">
                    <h1 class="section-heading col-12">Edit "Clients"</h1>
                    <b-table id="clients-table"
                             :busy="loading"
                             :items="sortedItems"
                             :fields="fields">
                        <template #cell(commands)="row">
                            <b-button size="sm" @click="deleteItem(row.item)" class="mr-2 delete-button">
                                Delete
                            </b-button>
                            <b-button size="sm" @click="editItem(row.item)" class="mr-2 edit-button">
                                Edit
                            </b-button>
                        </template>
                        <template #cell(logo)="row">
                            <img class="table-logo-image" :src="row.item.logo" :alt="row.item.name">
                        </template>
                        <template #cell(url)="row">
                            <a :href="row.item.url">{{row.item.url}}</a>
                        </template>
                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle"></b-spinner>
                                <strong>Loading...</strong>
                            </div>
                        </template>
                    </b-table>
                </div>
                <div class="buttons-container float-right">
                    <b-button @click="onAddNew" class="submit-button">Add new client</b-button>
                </div>
            </div>
        </div>
        <b-modal ref="editPopup"
                 :no-close-on-backdrop="true"
                 title="Edit/Create client"
                 @close="onPopupClose"
                 @ok="onEditSave"
                 @cancel="onPopupCancel">
            <b-overlay :show="updating">
                <form v-if="item">
                    <b-form-group label="Name:"
                                    label-for="name"
                                    invalid-feedback="Client name is required. Enter at least 2 letters."
                                    :state="nameState">
                        <b-form-input id="name"
                                    v-model="item.name"
                                    required
                                    :state="nameState"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="URL:"
                                    label-for="url">
                        <b-form-input id="url"
                                    v-model="item.url"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Logo:"
                                    label-for="logo"
                                    invalid-feedback="Client logo is required."
                                    :state="logoState">
                        <b-form-file id="logo"
                                    v-model="logoFile"
                                    accept="image/jpeg, image/png, image/gif"
                                    :state="logoState"
                                    placeholder="Choose an image file or drop it here..."
                                    drop-placeholder="Drop file here..."
                                    @input="onFileChange">
                        </b-form-file>
                    </b-form-group>
                    <img class="edit-logo-image" :src="item.logoFile || item.logo" :alt="item.name">
                    <b-form-group label="Order number:"
                                    label-for="orderId"
                                    invalid-feedback="Order number should be greater than 0."
                                    :state="orderState">
                        <b-form-input id="orderId"
                                    v-model="item.orderId"
                                    required
                                    :state="orderState"
                                    type="number"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Visible on website:"
                                    label-for="visible">
                        <b-form-checkbox id="visible"
                                    v-model="item.visible"
                                    type="checkbox"></b-form-checkbox>
                    </b-form-group>
                </form>
            </b-overlay>
        </b-modal>

        <b-modal :no-close-on-backdrop="true"
                 ref="confirmDelete" 
                 title="Delete client from list"
                 @close="onPopupClose"
                 @ok="onConfirmDelete"
                 @cancel="onPopupCancel">
            <p v-if="item">
                Are you sure you want to delete <strong>"{{ item.name }}"</strong> from database?
            </p>
        </b-modal>
    </div>
</template>

<script>
import {  doc, deleteDoc, updateDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "../../firebase";

export default {
    name: "EditClients",
    data() {
        return {
            inEditing: false,
            updating: false,
            fields: [
                { key: 'orderId', label: 'Order' },
                'name',
                'url',
                'logo',
                'visible',
                'commands'
            ],
            item: null,
            logoFile: null
        }
    },
    computed: {
        loading() {
            return this.inEditing || !this.items || this.items.length === 0;
        },
        items() {
            return this.$store.state.customers;
        },
        rows() {
            if (this.items) {
                return this.items.length;
            } else {
                return 0;
            }
        },
        sortedItems() {
            return this.items.slice().sort((a, b) => { return a.orderId - b.orderId });
        },
        nameState() {
            return this.item.name.length > 1 ? true : false;
        },
        logoState() {
            return !!this.logoFile || this.item.logo.length > 0;
        },
        orderState() {
            return this.item.orderId > 0 ? true : false;
        },
    },
    methods: {
        deleteItem(item) {
            if (item && this.confirmDelete) {
                this.item = item;
                this.confirmDelete.show();
            }
        },
        editItem(item) {
            if (item && this.editPopup) {
                this.item = JSON.parse(JSON.stringify(item));
                this.editPopup.show();
            }
        },
        async onConfirmDelete() {
            let index;

            if (this.item) {
                try {
                    this.inEditing = true;

                    const desertRef = ref(storage, this.item.fileKey);

                    await deleteObject(desertRef);
                    await deleteDoc(doc(db, "customers", this.item.id));

                    index = this.items.findIndex(item => item.id === this.item.id);
                    this.items.splice(index, 1);

                    alert(this.item.name + " successfully deleted.");
                    this.item = null;
                } catch {
                    alert("Customer delete failed.");
                } finally {
                    this.inEditing = false;
                }
            }
        },
        async onEditSave(e) {
            e.preventDefault();

            if (this.item && this.nameState && this.logoState && this.orderState) {
                let id = this.item.id,
                    docRef, index, clone;

                this.updating = true;
                this.item.orderId = Number(this.item.orderId);

                if (this.logoFile) {
                    try {
                        const file = this.logoFile,
                            storageRef = ref(storage, file.name),
                            metadata = {
                                contentType: file.type
                            };

                        await uploadBytes(storageRef, file, metadata);
                        this.item.logo = await getDownloadURL(storageRef);
                        this.item.fileKey = file.name;
                        delete this.item.logoFile;
                    } catch {
                        alert("Customer logo upload failed.");
                    }
                }

                if (id) {
                    // update item
                    try {
                        clone = Object.assign({}, this.item);
                        delete clone.id;

                        docRef = doc(db, "customers", id);
                        await updateDoc(docRef, clone);

                        index = this.items.findIndex(item => item.id === id);
                        this.$set(this.items, index, JSON.parse(JSON.stringify(this.item)));

                        alert(this.item.name + " successfully updated.");
                        this.item = null;
                        this.logoFile = null;
                        this.editPopup.hide();
                    } catch (e) {
                        alert("Customer update failed.");
                    } finally {
                        this.updating = false;
                    }
                } else {
                    // create item
                    try {
                        docRef = doc(db, "customers", this.item.name);

                        await setDoc(docRef, this.item);

                        this.item.id = docRef.id;

                        this.items.push(JSON.parse(JSON.stringify(this.item)));

                        alert(this.item.name + " successfully created.");

                        this.item = null;
                        this.logoFile = null;
                        this.editPopup.hide();
                    } catch {
                        alert("Customer create failed.");
                    } finally {
                        this.updating = false;
                    }
                }
            } else {
                alert('Name, Logo and Order number should be provided for each client.');
            }
        },
        onPopupClose() {
            this.item = null;
            this.logoFile = null;
        },
        onPopupCancel() {
            this.item = null;
            this.logoFile = null;
        },
        onAddNew() {
            this.editItem({
                orderId: 0,
                name: '',
                url: '',
                visible: true,
                logo: ''
            });
        },
        onFileChange() {
            if (this.item && !!this.logoFile) {
                this.item.logoFile = URL.createObjectURL(this.logoFile);
            }
            
            if (!this.logoFile) {
                this.item.logoFile = '';
            }
        }
    },
    mounted() {
        if (!auth.currentUser) {
            this.$router.push('/admin');
        }

        this.editPopup = this.$refs['editPopup'];
        this.confirmDelete = this.$refs['confirmDelete'];
    }
}
</script>

<style scoped>
    .table-logo-image {
        max-height: 50px;
        max-width: 250px;
    }

    .edit-logo-image {
        max-height: 200px;
        max-width: 200px;
        margin-bottom: 1em;
    }
</style>